import { Component, Inject, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { BaseComponent, CommonService, Shared } from "@rubii/frontend-common";
import { ConfigService } from "src/app/services/config.service";
import { RoleService } from "src/app/services/role.service";
import { AdvertiserAndAgency } from "../interfaces/advertiser-and-agency.interface";
import { ExUserAccessSource } from "../interfaces/ex-user-access-source.interface";
import { ClientService } from "../../services/client.service";

@Component({
    selector: 'app-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss']
})
export class EditUserComponent extends BaseComponent implements OnInit {
    constructor(
        private config: ConfigService,
        private roleService: RoleService,
        private client: ClientService,
        private snack: MatSnackBar,
        private dialogRef: MatDialogRef<EditUserComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any) {
        super();
    }

    userForm: FormGroup;

    includeServiceFee = true;

    canSeeMargin = true;

    selectedRole: any;

    advertiserAndAgency: AdvertiserAndAgency[] = [];
    agencyMap: Map<number, AdvertiserAndAgency> = new Map();
    advertiserMap: Map<number, AdvertiserAndAgency> = new Map();
    advertiserAndAgencyFlatData = [];
    selectedAdvertiserAndAgency = [];

    selectedAccessSource = {
        advertisers: [],
        agencies: []
    };

    advertiserAgencyLoaded = false;
    externalUserAccessSourceLoaded = false;

    exUserAccessedSource: ExUserAccessSource[] = [];

    ngOnInit(): void {
        this.initForm();
        this.getExternalUserAccessSources();

        setTimeout(() => {
            const elements = document.getElementsByClassName('mat-dialog-container');
            const element = elements.item(0) as HTMLElement;
            element.style.padding = '0px';
        });
    }

    onExUserAccessSourceChange(event: { advertisers: AdvertiserAndAgency[]; agencies: AdvertiserAndAgency[] }) {
        this.selectedAccessSource = {
            advertisers: [],
            agencies: []
        };

        for (const ad of event.advertisers) {
            this.selectedAccessSource.advertisers.push({ adId: ad.id, agencyId: ad.agencyId });
        }
        for (const agency of event.agencies) {
            this.selectedAccessSource.agencies.push(agency.id);
        }
    }

    toggleIncludeServiceFee() {
        this.includeServiceFee = !this.includeServiceFee;
        this.userForm.get('includeServiceFee').setValue(this.includeServiceFee);
    }

    toggleCanSeeMargin() {
        this.canSeeMargin = !this.canSeeMargin;
        this.userForm.get('canSeeMargin').setValue(this.canSeeMargin);
    }

    handleRoleSelected(data) {
        this.selectedRole = data;
        if (!data) {
            return ;
        }
        let roleIds = [];
        if (this.selectedRole?.id === Shared.dicts.UserRoles.AM || this.selectedRole?.id === Shared.dicts.UserRoles.TRADER) {
            roleIds.push(...[Shared.dicts.UserRoles.AM, Shared.dicts.UserRoles.TRADER]);
        } else {
            roleIds.push(this.selectedRole.id);
        }
        roleIds = [...new Set(roleIds)];
        this.userForm.get('roles').setValue(roleIds);
        if (this.selectedRole && this.selectedRole.id === Shared.dicts.UserRoles.EXTERNAL) {
            this.userForm.addControl('includeServiceFee', new FormControl(null, [Validators.required]));
            this.userForm.get('includeServiceFee').setValue(this.includeServiceFee);
        } else if ((this.selectedRole && this.selectedRole.id !== Shared.dicts.UserRoles.EXTERNAL) && this.userForm.contains('includeServiceFee')) {
            this.userForm.removeControl('includeServiceFee');
        }
    }

    save(event) {
        if (!this.userForm.valid) {
            this.openSnackBarWarning(this.snack, 'Please enter valid user information before saving.', 'close');
            return;
        }
        if (this.selectedRole.id === Shared.dicts.UserRoles.EXTERNAL && (!this.selectedAccessSource.advertisers.length && !this.selectedAccessSource.agencies.length)) {
            this.openSnackBarWarning(this.snack, 'Please enter valid user information before saving.', 'close');
            return;
        }
        let canSeeMargin = 1;
        if (this.selectedRole.id === Shared.dicts.UserRoles.EXTERNAL) {
            canSeeMargin = 0;
        } else if (this.selectedRole.id === Shared.dicts.UserRoles.ADMIN) {
            canSeeMargin = 1;
        } else {
            canSeeMargin = this.userForm.value.canSeeMargin ? 1 : 0;
        }
        let input = {
            firstname: this.userForm.value.firstname,
            lastname: this.userForm.value.lastname,
            email: this.userForm.value.email,
            roles: this.userForm.value.roles,
            canSeeMargin,
            advertisers: this.selectedAccessSource.advertisers,
            agencies: this.selectedAccessSource.agencies,
            includeServiceFee: this.userForm.value.roles.includes(Shared.dicts.UserRoles.EXTERNAL) ? this.includeServiceFee : null
        };
        this.dialogRef.close(input);
    }

    close() {
        this.dialogRef.close();
    }

    initForm() {
        let firstname = '';
        let lastname = '';
        if (this.data && this.data.name) {
            const temp = this.data.name.split(' ');
            firstname = temp[0];
            lastname = temp[1];
        }
        this.userForm = new FormGroup({
            firstname: new FormControl(firstname, [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(45)]),
            lastname: new FormControl(lastname, [ 
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(45)]),
            email: new FormControl(this.data ? this.data.email : '', [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(100),
                Validators.email]),
            canSeeMargin: new FormControl(this.canSeeMargin),
            roles: new FormControl(this.data ? this.data.roles : [], [Validators.required]),
        });
        if (this.data && this.data.id) {
            this.selectedRole = this.roleService.getUserRoleByRoleId(this.data.roles[0]);
            if (this.data.roles.includes(Shared.dicts.UserRoles.EXTERNAL)) {
                this.includeServiceFee = this.data.includeServiceFee === null ? true : this.data.includeServiceFee;
                this.userForm.addControl('includeServiceFee', new FormControl(this.data ? this.data.includeServiceFee : true, [Validators.required]));
                this.userForm.get('includeServiceFee').setValue(this.includeServiceFee);
            }
            this.canSeeMargin = this.data.canSeeMargin === null || this.data.canSeeMargin === undefined ? true: !!this.data.canSeeMargin;
            this.userForm.get('canSeeMargin').setValue(this.canSeeMargin);
        }
    }

    getExternalUserAccessSources() {
        this.externalUserAccessSourceLoaded = false;
        if (!this.data || !this.data.id) {
            this.externalUserAccessSourceLoaded = true;
            return;
        }
        this.client.user.getExternalUserAccess(this.data.id).then(res => this.exUserAccessedSource = res).catch(e => this.exUserAccessedSource = []);
    }
}